/* eslint-disable react/react-in-jsx-scope */
import type { AppTheme } from '@backstage/core-plugin-api';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  genPageTheme,
  shapes,
} from '@backstage/theme';
import Brightness2 from '@material-ui/icons/Brightness2';
import WbSunny from '@material-ui/icons/WbSunny';

import { darkPalette, lightPalette, pageGradients } from './palettes';

type GradientSet = (typeof pageGradients)[keyof typeof pageGradients];

const fontFamily =
  "'Outfit Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function createProductTheme(
  palette: typeof lightPalette | typeof darkPalette,
  gradient: GradientSet,
) {
  // Card shape: bordered, ambient shadow, radius. CSS vars come from
  // theme.css so this MUI side and the BUI Card component (styled
  // directly in theme.css) stay in sync from one source.
  //
  // MUI v4 Paper renders `rounded` (border-radius) and `elevationN`
  // (box-shadow) as *separate* classes from `root`, all applied to the
  // same element simultaneously — a plain `styleOverrides.root` override
  // never reaches either one (verified live: a bare Paper showed 4px
  // corners and MUI's default elevation shadow despite a root override
  // setting both). Card renders as a non-square Paper with elevation=1
  // by default, so it has the exact same gap — just less visible, since
  // its border and hover glow (a `:hover` compound selector, which beats
  // a plain `.MuiPaper-elevationN` class on specificity alone) were the
  // obvious signal, masking a wrong *resting-state* radius/shadow.
  //
  // Fixing this once at the MuiPaper level (below) is the actual source
  // of truth for shape on any Paper-based surface — Card included, since
  // it carries `.MuiPaper-root`/`-rounded`/`-elevation1` regardless of
  // its own MuiCard-specific overrides.
  const cardShape = {
    borderRadius: 'var(--bui-radius-4)',
    border: `1px solid ${palette.border}`,
    boxShadow: 'var(--card-shadow)',
  };

  // elevation0 means "flat" by convention; every other level collapses to
  // one ambient shadow — this theme has no equivalent to MUI's 24-step
  // elevation scale.
  const paperElevations = Object.fromEntries(
    Array.from({ length: 25 }, (_, elevation) => [
      `elevation${elevation}`,
      { boxShadow: elevation === 0 ? 'none' : 'var(--card-shadow)' },
    ]),
  );

  return createUnifiedTheme({
    palette,
    defaultPageTheme: 'home',
    fontFamily,
    pageTheme: {
      home: genPageTheme({ colors: [...gradient.home], shape: shapes.wave2 }),
      documentation: genPageTheme({
        colors: [...gradient.documentation],
        shape: shapes.wave,
      }),
      apis: genPageTheme({ colors: [...gradient.apis], shape: shapes.wave2 }),
    },
    components: {
      // Static shape (border, radius, shadow) comes entirely from MuiPaper
      // below, which cascades correctly since Card carries Paper's classes
      // regardless. This only adds what's genuinely Card-specific: the
      // hover transform/glow (a `:hover` compound selector, which reliably
      // beats a plain `.MuiPaper-elevationN` class on specificity, unlike
      // the resting-state shadow) and the transition that animates it.
      MuiCard: {
        styleOverrides: {
          root: {
            transition:
              'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              borderColor: palette.primary.main,
              boxShadow: 'var(--card-shadow-hover)',
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      // The actual source of truth for shape on any Paper-based surface
      // without a more specific override below — Card included (see the
      // comment on `cardShape` above), plus e.g. @material-table/core's
      // default container, used by the Jira/CI-CD entity tabs and the
      // Agents/Skills list pages via @backstage/core-components's Table.
      MuiPaper: {
        styleOverrides: {
          root: { ...cardShape },
          rounded: { borderRadius: 'var(--bui-radius-4)' },
          ...paperElevations,
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontFamily: 'var(--bui-font-monospace)',
            fontSize: 11,
            fontWeight: 600,
            color: palette.textSubtle,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'var(--bui-bg-neutral-1-hover)',
            },
          },
        },
      },

      // Match BUI's own Button/Chip radius (--bui-radius-2, bumped to 8px
      // in theme.css) so both button flavors on a page look the same.
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 'var(--bui-radius-2)' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 'var(--bui-radius-2)' },
        },
      },
      // BUI's own TextField already uses --bui-radius-2; match it here so
      // MUI-rendered inputs (used directly in this app and throughout
      // scaffolder/catalog forms) don't look flatter/squarer beside it.
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 'var(--bui-radius-2)' },
        },
      },

      // MUI's Dialog/Popover (which backs Menu, Select's dropdown, and
      // Autocomplete) are Paper-based floating surfaces with no BUI
      // equivalent token path — mirror BUI's own Dialog/Menu/Popover shape
      // (bordered, --bui-radius-3, shared elevation shadow) directly so
      // they don't look like a different design system next to BUI's.
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--bui-radius-3)',
            border: `1px solid ${palette.border}`,
            boxShadow: 'var(--card-shadow)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--bui-radius-3)',
            border: `1px solid ${palette.border}`,
            boxShadow: 'var(--card-shadow)',
          },
        },
      },

      // MUI's Tooltip is a different component from BUI's own Tooltip but
      // both appear throughout the app; MUI's default hardcodes a dark
      // grey background regardless of theme, so match BUI Tooltip's
      // bordered/bg-app look and its 4px radius exactly.
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.background.paper,
            color: palette.textContrast,
            border: `1px solid ${palette.border}`,
            boxShadow: 'var(--card-shadow)',
            borderRadius: 4,
          },
        },
      },
    },
  });
}

const themes = {
  dark: createProductTheme(darkPalette, pageGradients.default),
  light: createProductTheme(lightPalette, pageGradients.default),
};

function themeProvider(theme: (typeof themes)[keyof typeof themes]) {
  return ({ children }: { children: React.ReactNode }) => (
    <UnifiedThemeProvider theme={theme} children={children} />
  );
}

// Dark is listed first so it wins as the default theme.
export const customThemes: (Partial<AppTheme> & Omit<AppTheme, 'theme'>)[] = [
  {
    id: 'dark',
    title: 'Dark',
    variant: 'dark',
    icon: <Brightness2 />,
    Provider: themeProvider(themes.dark),
  },
  {
    id: 'light',
    title: 'Light',
    variant: 'light',
    icon: <WbSunny />,
    Provider: themeProvider(themes.light),
  },
];
